/**
 * useAcceptInvitation
 *
 * TanStack Query mutation hook for accepting invitations (existing users).
 *
 * ## Features
 * - **Mutation**: Calls POST /users/accept-invitation
 * - **Error handling**: Built-in error state management
 * - **Loading state**: Provides isPending indicator
 *
 * @param options - Callbacks for success and error handling
 * @param options.isAuthenticated - Whether the user has a stored session
 * @example
 * ```tsx
 * const { mutate: accept, isPending } = useAcceptInvitation({
 *   isAuthenticated: isLoggedIn,
 *   onSuccess: (data) => {
 *     console.log('Invitation accepted:', data.message);
 *     navigate('/home');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to accept invitation:', error);
 *   }
 * });
 *
 * accept({ inviteId: '123', token: 'abc' });
 * ```
 *
 * @since 1.0.0
 * @category Hooks/Mutation
 */
import { useMutation } from '@tanstack/react-query';
import { acceptInvitation } from '@/features/auth/services/invitation';
import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
} from '@/features/auth/types/invitation.types';

interface UseAcceptInvitationOptions {
  isAuthenticated: boolean;
  onSuccess?: (data: AcceptInvitationResponse) => void;
  onError?: (error: Error) => void;
}

export function useAcceptInvitation(options?: UseAcceptInvitationOptions) {
  return useMutation<AcceptInvitationResponse, Error, AcceptInvitationRequest>({
    mutationFn: async (payload) => {
      if (!options?.isAuthenticated) {
        throw new Error('Missing user session');
      }

      return await acceptInvitation(payload);
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export default useAcceptInvitation;
